import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

export interface SyncMeta {
  lastSyncVersion: number;
  lastSyncAt: string;
  bootstrapComplete: boolean;
}

export interface PendingMutation {
  id: string;
  entity: string;
  operation: 'create' | 'update' | 'delete';
  entityId: string;
  data?: Record<string, unknown>;
  clientTimestamp: string;
  retryCount: number;
}

interface CrmDBSchema extends DBSchema {
  contacts: { key: string; value: Record<string, unknown>; indexes: { 'by-sync-version': number } };
  deals: { key: string; value: Record<string, unknown>; indexes: { 'by-sync-version': number } };
  dealTypes: { key: string; value: Record<string, unknown>; indexes: { 'by-sync-version': number } };
  projects: { key: string; value: Record<string, unknown>; indexes: { 'by-sync-version': number } };
  tasks: { key: string; value: Record<string, unknown>; indexes: { 'by-sync-version': number } };
  notes: { key: string; value: Record<string, unknown>; indexes: { 'by-sync-version': number } };
  noteAttachments: { key: string; value: Record<string, unknown>; indexes: { 'by-sync-version': number } };
  auditLogs: { key: string; value: Record<string, unknown>; indexes: { 'by-sync-version': number } };
  users: { key: string; value: Record<string, unknown> };
  syncMeta: { key: string; value: SyncMeta };
  pendingMutations: { key: string; value: PendingMutation; indexes: { 'by-created': string } };
}

const DB_VERSION = 4;

export class CrmDatabase {
  private db: IDBPDatabase<CrmDBSchema> | null = null;
  private dbName: string;

  constructor(orgId: string) {
    this.dbName = `crm-${orgId}`;
  }

  async open(): Promise<void> {
    if (this.db) return;
    this.db = await openDB<CrmDBSchema>(this.dbName, DB_VERSION, {
      upgrade(db, oldVersion, _newVersion, transaction) {
        // Entity stores with sync-version index
        const entityStores = ['contacts', 'deals', 'dealTypes', 'projects', 'tasks', 'notes', 'noteAttachments', 'auditLogs'] as const;
        for (const name of entityStores) {
          if (!db.objectStoreNames.contains(name)) {
            const store = db.createObjectStore(name, { keyPath: 'id' });
            store.createIndex('by-sync-version', 'syncVersion');
          }
        }
        if (!db.objectStoreNames.contains('users')) {
          db.createObjectStore('users', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('syncMeta')) {
          db.createObjectStore('syncMeta');
        }
        if (!db.objectStoreNames.contains('pendingMutations')) {
          const store = db.createObjectStore('pendingMutations', { keyPath: 'id' });
          store.createIndex('by-created', 'clientTimestamp');
        }

        // v2: Clear cached data to force re-bootstrap (fixes enum values stored as strings)
        if (oldVersion >= 1 && oldVersion < 2) {
          for (const name of entityStores) {
            if (db.objectStoreNames.contains(name)) {
              transaction.objectStore(name).clear();
            }
          }
          if (db.objectStoreNames.contains('users')) {
            transaction.objectStore('users').clear();
          }
          if (db.objectStoreNames.contains('syncMeta')) {
            transaction.objectStore('syncMeta').clear();
          }
        }

        // v3+v4: Clear cached data to force re-bootstrap (auditLogs added, SyncVersion fix)
        if (oldVersion >= 1 && oldVersion < 4) {
          for (const name of entityStores) {
            if (db.objectStoreNames.contains(name)) {
              transaction.objectStore(name).clear();
            }
          }
          if (db.objectStoreNames.contains('users')) {
            transaction.objectStore('users').clear();
          }
          if (db.objectStoreNames.contains('syncMeta')) {
            transaction.objectStore('syncMeta').clear();
          }
        }
      },
    });
  }

  private ensureDb(): IDBPDatabase<CrmDBSchema> {
    if (!this.db) throw new Error('Database not open. Call open() first.');
    return this.db;
  }

  // Read multiple entity stores in a single readonly transaction.
  // IDB loads the database file from disk on the first transaction after a page
  // reload. By combining all reads into one transaction we pay that cost once.
  async getAllMultiple<K extends 'contacts' | 'deals' | 'dealTypes' | 'projects' | 'tasks' | 'notes' | 'noteAttachments' | 'auditLogs' | 'users'>(
    stores: K[]
  ): Promise<Record<K, Record<string, unknown>[]>> {
    const db = this.ensureDb();
    const tx = db.transaction(stores, 'readonly');
    const results = {} as Record<K, Record<string, unknown>[]>;
    const promises: Promise<void>[] = [];
    for (const store of stores) {
      promises.push(
        tx.objectStore(store).getAll().then(records => {
          results[store] = records;
        })
      );
    }
    await Promise.all(promises);
    return results;
  }

  // Generic entity operations
  async getAll<K extends 'contacts' | 'deals' | 'dealTypes' | 'projects' | 'tasks' | 'notes' | 'noteAttachments' | 'auditLogs' | 'users'>(
    store: K
  ): Promise<Record<string, unknown>[]> {
    return this.ensureDb().getAll(store);
  }

  async get<K extends 'contacts' | 'deals' | 'dealTypes' | 'projects' | 'tasks' | 'notes' | 'noteAttachments' | 'auditLogs' | 'users'>(
    store: K,
    id: string
  ): Promise<Record<string, unknown> | undefined> {
    return this.ensureDb().get(store, id);
  }

  async put<K extends 'contacts' | 'deals' | 'dealTypes' | 'projects' | 'tasks' | 'notes' | 'noteAttachments' | 'auditLogs' | 'users'>(
    store: K,
    value: Record<string, unknown>
  ): Promise<void> {
    await this.ensureDb().put(store, value);
  }

  async putMany<K extends 'contacts' | 'deals' | 'dealTypes' | 'projects' | 'tasks' | 'notes' | 'noteAttachments' | 'auditLogs' | 'users'>(
    store: K,
    values: Record<string, unknown>[]
  ): Promise<void> {
    if (values.length === 0) return;
    const db = this.ensureDb();
    const tx = db.transaction(store, 'readwrite');
    // Fire all puts without awaiting individually — IDB transactions batch
    // writes internally. Awaiting each put serializes microtask round-trips
    // (2k sequential awaits for 2k contacts). Await only tx.done at the end.
    for (const value of values) {
      tx.store.put(value);
    }
    await tx.done;
  }

  // Apply a mix of puts and deletes to a single store in one transaction.
  // Used by delta sync — avoids N IDB transactions for N changes.
  async applyChangesToStore<K extends 'contacts' | 'deals' | 'dealTypes' | 'projects' | 'tasks' | 'notes' | 'noteAttachments' | 'auditLogs'>(
    store: K,
    puts: Record<string, unknown>[],
    deletes: string[]
  ): Promise<void> {
    if (puts.length === 0 && deletes.length === 0) return;
    const db = this.ensureDb();
    const tx = db.transaction(store, 'readwrite');
    for (const v of puts) tx.store.put(v);
    for (const id of deletes) tx.store.delete(id);
    await tx.done;
  }

  async delete<K extends 'contacts' | 'deals' | 'dealTypes' | 'projects' | 'tasks' | 'notes' | 'noteAttachments' | 'auditLogs' | 'users'>(
    store: K,
    id: string
  ): Promise<void> {
    await this.ensureDb().delete(store, id);
  }

  async clear<K extends 'contacts' | 'deals' | 'dealTypes' | 'projects' | 'tasks' | 'notes' | 'noteAttachments' | 'auditLogs' | 'users'>(
    store: K
  ): Promise<void> {
    await this.ensureDb().clear(store);
  }

  // Sync meta
  async getSyncMeta(): Promise<SyncMeta | undefined> {
    return this.ensureDb().get('syncMeta', 'default');
  }

  async setSyncMeta(meta: SyncMeta): Promise<void> {
    await this.ensureDb().put('syncMeta', meta, 'default');
  }

  // Pending mutations
  async addPendingMutation(mutation: PendingMutation): Promise<void> {
    await this.ensureDb().put('pendingMutations', mutation);
  }

  async getAllPendingMutations(): Promise<PendingMutation[]> {
    return this.ensureDb().getAllFromIndex('pendingMutations', 'by-created');
  }

  async removePendingMutation(id: string): Promise<void> {
    await this.ensureDb().delete('pendingMutations', id);
  }

  async clearPendingMutations(): Promise<void> {
    await this.ensureDb().clear('pendingMutations');
  }

  close(): void {
    this.db?.close();
    this.db = null;
  }

  async destroy(): Promise<void> {
    this.close();
    const { deleteDB } = await import('idb');
    await deleteDB(this.dbName);
  }
}
